/**
 * The demo, end to end: fill a form, watch it run, read the report.
 *
 * One screen with three states rather than three screens. Somebody being shown this should never
 * have to be told where to look next — the form stays on screen while the pipeline runs beneath it,
 * and the report appears under that.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { GatewayError, loadForm, startRun, toFileValue, uploadFile } from './gateway/client.js'
import { initialRunState, runReducer, type RunState } from './gateway/run-state.js'
import type { InputSpec } from './gateway/types.js'
import { Dashboard } from './signature/Dashboard.js'
import { Pipeline } from './signature/Pipeline.js'
import { reportFrom, type Report } from './signature/report.js'
import { emptyValues, partiesPayload, SignatureForm, validate, type FormValues } from './signature/SignatureForm.js'
import { loadSample, type Sample } from './signature/samples.js'
import './design/tokens.css'
import './design/app.css'
import './signature/signature.css'

type Boot = { status: 'loading' } | { status: 'ready'; specs: InputSpec[] } | { status: 'error'; message: string }

export default function App() {
  const [boot, setBoot] = useState<Boot>({ status: 'loading' })
  const [values, setValues] = useState<FormValues>(emptyValues)
  const [run, setRun] = useState<RunState>(initialRunState)
  const [report, setReport] = useState<Report | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stage, setStage] = useState<string>('')

  const abort = useRef<AbortController | null>(null)
  useEffect(() => () => abort.current?.abort(), [])

  useEffect(() => {
    void (async () => {
      try {
        setBoot({ status: 'ready', specs: await loadForm() })
      } catch (err) {
        setBoot({
          status: 'error',
          message:
            err instanceof GatewayError && err.status === 502
              ? 'Fabric is not reachable from this machine. Check that the engine and the BFF are running.'
              : err instanceof Error
                ? err.message
                : 'Could not load the workflow.',
        })
      }
    })()
  }, [])

  const useSample = useCallback(async (sample: Sample) => {
    setError(null)
    try {
      setValues({
        document: await loadSample(sample),
        parties: sample.parties,
        signingRule: sample.signingRule,
        samples: [],
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that sample.')
    }
  }, [])

  const specs = boot.status === 'ready' ? boot.specs : []
  const problems = validate(values, specs)
  const busy = run.phase === 'running'

  const go = async () => {
    setError(null)
    setReport(null)
    setRun({ ...initialRunState, phase: 'running' })
    setStage('Uploading…')

    const controller = new AbortController()
    abort.current = controller

    try {
      /*
       * ⚠️ Files are uploaded FIRST and separately, then referenced by id.
       *
       * A run's `inputs` cannot carry file bytes — the engine wants
       * `{transfer_method, upload_file_id, type}`, and all three fields are required. Doing this
       * before the run means an upload failure is reported as an upload failure, rather than
       * arriving inside the stream as a workflow error nobody can act on.
       */
      const inputs: Record<string, unknown> = {
        parties: partiesPayload(values.parties),
        signing_rule: values.signingRule,
      }

      if (values.document !== null) {
        inputs['document'] = toFileValue(await uploadFile(values.document))
      }
      if (values.samples.length > 0) {
        setStage('Uploading samples…')
        inputs['sample_signatures'] = (await Promise.all(values.samples.map(uploadFile))).map(toFileValue)
      }

      setStage('Running…')
      let finished: RunState = { ...initialRunState, phase: 'running' }
      await startRun({
        inputs,
        signal: controller.signal,
        onFrame: (frame) => {
          finished = runReducer(finished, frame)
          // Snapshotted per frame; React batches the renders. A twenty-page run emits a few hundred
          // frames over a minute, which is nowhere near needing rAF throttling.
          setRun(finished)
        },
      })

      setStage('')
      const parsed = reportFrom(finished.outputs)
      if (finished.phase === 'failed') {
        setError(finished.error ?? 'The workflow did not finish. The report below may be incomplete.')
      } else if (parsed === null) {
        /*
         * ⚠️ Succeeded but unreadable is its own state, and worth saying precisely: the run WORKED
         * and this app could not parse what came back. That is a contract change, not a failure —
         * and blaming the workflow for it would send somebody debugging the wrong thing.
         */
        setError('The run finished but did not return a report this demo can read. The workflow’s End output may have changed.')
      }
      setReport(parsed)
    } catch (err) {
      if (controller.signal.aborted) return
      setRun((r) => ({ ...r, phase: 'failed' }))
      setStage('')
      setError(err instanceof Error ? err.message : 'The run could not be started.')
    }
  }

  return (
    <>
      <header className="topbar">
        <span className="topbar__brand">kairhos</span>
        <span className="topbar__sep" aria-hidden="true">
          ·
        </span>
        <span className="topbar__what">Signature validation</span>
        <span className="topbar__spacer" />
        <span className="topbar__state">powered by Agentic Fabric</span>
      </header>

      <main className="page">
        <h1>Has this document been signed by everyone it should be?</h1>
        <p className="lede">
          Upload a contract and say who was supposed to sign it. Every page is rendered and read by a
          vision model in parallel, then reduced to one report — which fails closed: anything unclear
          blocks a pass rather than being rounded up into one.
        </p>

        {boot.status === 'loading' && <p className="lede">Loading the workflow…</p>}

        {boot.status === 'error' && (
          <div className="notice notice--error">
            <strong>Cannot reach the workflow.</strong> {boot.message}
          </div>
        )}

        {boot.status === 'ready' && (
          <>
            <section className="card">
              <div className="card__head">
                <span className="card__title">The document</span>
                <span className="card__hint">{specs.length} inputs declared by the workflow</span>
              </div>
              <div className="card__body">
                <SignatureForm
                  specs={specs}
                  values={values}
                  onChange={setValues}
                  onUseSample={(s) => void useSample(s)}
                  disabled={busy}
                />
              </div>
            </section>

            {error !== null && <div className="notice notice--error">{error}</div>}

            <div className="go">
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy || problems.length > 0}
                onClick={() => void go()}
                data-run
              >
                {busy ? stage || 'Working…' : 'Check the document'}
              </button>
              {/* The first blocking reason, beside the button rather than in a list nobody reads. */}
              {problems.length > 0 && <span className="go__why">{problems[0]}</span>}
              {busy && (
                <button type="button" className="btn" onClick={() => abort.current?.abort()}>
                  Stop
                </button>
              )}
            </div>

            {run.phase !== 'idle' && (
              <section className="card">
                <div className="card__head">
                  <span className="card__title">What the pipeline is doing</span>
                  {run.elapsed !== null && <span className="card__hint">{run.elapsed.toFixed(1)}s</span>}
                </div>
                <div className="card__body">
                  <Pipeline state={run} />
                </div>
              </section>
            )}

            {report !== null && (
              <section className="card">
                <div className="card__head">
                  <span className="card__title">AI observations</span>
                </div>
                <div className="card__body">
                  <Dashboard report={report} />
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </>
  )
}
